import type { ScheduleId } from '../types'

export interface ScheduleMutationError {
  code: string
  message: string
  switchboardNames?: string[]
}

const SCHEDULE_IN_USE_BY_SWITCHBOARD_MESSAGE =
  'This schedule cannot be deleted because it is in use by a switchboard. Remove it from the switchboard and try again.'

function getObject(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null
}

function getNonEmptyString(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : undefined
}

function extractSwitchboardNames(data: Record<string, unknown>): string[] {
  const legacyAdminLinks = getObject(data.legacy_admin_links)
  if (!legacyAdminLinks) return []

  const switchboardLinks = getObject(legacyAdminLinks.switchboard_links)
  if (!switchboardLinks) return []

  return Object.keys(switchboardLinks)
    .map((name) => name.trim())
    .filter((name) => name.length > 0)
}

function summarizeSwitchboardNames(names: string[]): string {
  if (names.length <= 3) return names.join(', ')

  const visibleNames = names.slice(0, 3).join(', ')
  const remaining = names.length - 3
  return `${visibleNames}, and ${remaining} more`
}

function buildSwitchboardInUseMessage({
  switchboardNames,
  fallbackMessage,
}: {
  switchboardNames: string[]
  fallbackMessage?: string
}): string {
  if (switchboardNames.length === 0) {
    return fallbackMessage ?? SCHEDULE_IN_USE_BY_SWITCHBOARD_MESSAGE
  }

  const switchboardLabel =
    switchboardNames.length === 1 ? 'switchboard' : 'switchboards'
  const removeTarget =
    switchboardNames.length === 1 ? 'that switchboard' : 'those switchboards'

  return `This schedule cannot be deleted because it is used in ${switchboardLabel}: ${summarizeSwitchboardNames(switchboardNames)}. Remove it from ${removeTarget} and try again.`
}

function extractScheduleErrorData(data: unknown): {
  code?: string
  message?: string
  switchboardNames: string[]
} {
  if (typeof data === 'string') {
    return { message: getNonEmptyString(data), switchboardNames: [] }
  }

  const objectData = getObject(data)
  if (!objectData) return { switchboardNames: [] }

  const topLevelCode = getNonEmptyString(objectData.code)
  const topLevelMessage =
    getNonEmptyString(objectData.message) ??
    getNonEmptyString(objectData.detail) ??
    getNonEmptyString(objectData.error)
  const errorList = Array.isArray(objectData.errors) ? objectData.errors : []
  const firstError = getObject(errorList[0])
  const nestedCode = firstError ? getNonEmptyString(firstError.code) : undefined
  const nestedMessage = firstError
    ? getNonEmptyString(firstError.detail)
    : undefined

  return {
    code: topLevelCode ?? nestedCode,
    message: topLevelMessage ?? nestedMessage,
    switchboardNames: extractSwitchboardNames(objectData),
  }
}

function isSwitchboardInUseError({
  code,
  message,
  switchboardNames,
}: {
  code?: string
  message?: string
  switchboardNames?: string[]
}): boolean {
  const normalizedCode = code?.toLowerCase() ?? ''
  const normalizedMessage = message?.toLowerCase() ?? ''

  const codeMatches =
    normalizedCode.includes('schedule_in_use') ||
    (normalizedCode.includes('switchboard') &&
      (normalizedCode.includes('in_use') || normalizedCode.includes('in-use')))
  const hasDeleteSemantics =
    normalizedMessage.includes('delete') ||
    normalizedMessage.includes('deleted')
  const hasInUseSemantics =
    normalizedMessage.includes('in use') ||
    normalizedMessage.includes('used by') ||
    normalizedMessage.includes('used in') ||
    normalizedMessage.includes('assigned to')
  const messageMatches =
    normalizedMessage.includes('switchboard') &&
    hasDeleteSemantics &&
    hasInUseSemantics
  const hasSwitchboardLinks = (switchboardNames?.length ?? 0) > 0

  return codeMatches || messageMatches || hasSwitchboardLinks
}

export function buildDeleteScheduleError(
  id: ScheduleId,
  data: unknown,
): ScheduleMutationError {
  const parsedError = extractScheduleErrorData(data)

  if (isSwitchboardInUseError(parsedError)) {
    return {
      code: 'schedule_in_use_by_switchboard',
      message: buildSwitchboardInUseMessage({
        switchboardNames: parsedError.switchboardNames,
        fallbackMessage: parsedError.message,
      }),
      switchboardNames: parsedError.switchboardNames,
    }
  }

  if (parsedError.message) {
    return {
      code: parsedError.code ?? 'delete_error',
      message: parsedError.message,
    }
  }

  return {
    code: 'delete_error',
    message: `Failed to delete schedule ${id}`,
  }
}
